import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Sunrise, CalendarClock, CalendarRange, Loader2, ShieldAlert, Target, Sparkles, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";

const HORIZONS = [
  { key: "annual",  label: "Année",   Icon: Compass,       tone: "#0B2545", accent: "#EEF4FF", tid: "coach-h-annual" },
  { key: "monthly", label: "Mois",    Icon: CalendarRange, tone: "#7A5B14", accent: "#FBF6EA", tid: "coach-h-monthly" },
  { key: "weekly",  label: "Semaine", Icon: CalendarClock, tone: "#0B7A3B", accent: "#DCFCE7", tid: "coach-h-weekly" },
  { key: "daily",   label: "Aujourd'hui", Icon: Sunrise,   tone: "#B45309", accent: "#FEF3C7", tid: "coach-h-daily" },
];

export default function AbiCoach() {
  const [plan, setPlan] = useState(null);
  const [blocking, setBlocking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/abi/copilot/plan"),
      api.get("/abi/copilot/blocking-habits"),
    ]).then(([p, b]) => {
      setPlan(p.data);
      setBlocking(b.data);
    }).catch(() => toast.error("Erreur de chargement du Coach."))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !plan) return (
    <div className="p-8 flex items-center gap-2" data-testid="abi-coach-loading">
      <Loader2 size={16} className="animate-spin" /> Chargement du plan…
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FBF6EA" }} data-testid="abi-coach-root">
      <TopBar />

      {/* Hero */}
      <div className="px-8 pt-8 pb-6"
            style={{ background: "linear-gradient(120deg,#0B2545 0%,#123B6E 60%,#1D5AA5 100%)" }}>
        <div className="max-w-6xl mx-auto text-ivory">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest opacity-80">
            <Target size={14} /> ALTIORA · Coach de Performance
          </div>
          <h1 className="text-3xl md:text-4xl font-serif mt-2 leading-tight text-white"
              data-testid="abi-coach-title">
            Votre plan de progression — de l'année à aujourd'hui
          </h1>
          <p className="text-white/85 mt-3 max-w-3xl">{plan.coherence_note_fr}</p>
          <div className="mt-3">
            <span className="px-3 py-1 rounded-full text-[13px]"
                  style={{ backgroundColor: "rgba(197,168,128,0.20)", color: "#F5D687" }}>
              Profession · {plan.profession_label_fr}
            </span>
          </div>
        </div>
      </div>

      {/* 4 horizons vertical cascade */}
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-4">
        {HORIZONS.map(h => {
          const layer = plan[h.key];
          if (!layer) return null;
          const Icon = h.Icon;
          return (
            <div key={h.key} data-testid={h.tid}
                  className="rounded-lg shadow-sm p-6 bg-white border-l-4"
                  style={{ borderLeftColor: h.tone }}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: h.accent, color: h.tone }}>
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: h.tone }}>
                    Horizon · {h.label}
                  </div>

                  {h.key === "annual" && (
                    <>
                      <h2 className="font-serif text-xl mt-1" style={{ color: "#0B2545" }}>
                        {layer.theme_fr}
                      </h2>
                      {layer.mission_fr && (
                        <p className="text-[13px] italic text-slate-600 mt-1">
                          « {layer.mission_fr} »
                        </p>
                      )}
                      {(layer.top_skill_gaps || []).length > 0 && (
                        <div className="mt-3 text-[13px] space-y-1">
                          <div className="uppercase text-[10px] tracking-widest font-bold" style={{ color: "#7A5B14" }}>
                            3 compétences à travailler cette année
                          </div>
                          {layer.top_skill_gaps.map((g, i) => (
                            <div key={i} className="pl-4 text-slate-800">· {g}</div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {h.key === "monthly" && (
                    <>
                      <h2 className="font-serif text-xl mt-1" style={{ color: "#0B2545" }}>
                        Milestones de ce mois
                      </h2>
                      <ul className="mt-2 space-y-1 text-[14px]">
                        {(layer.milestones_fr || []).map((m, i) => (
                          <li key={i} className="text-slate-800">→ {m}</li>
                        ))}
                      </ul>
                      {Object.keys(layer.kpi_targets || {}).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2 text-[12px]">
                          {Object.entries(layer.kpi_targets).map(([kpi, v]) => (
                            <span key={kpi} className="px-2 py-0.5 rounded"
                                  style={{ backgroundColor: h.accent, color: h.tone }}>
                              {kpi} · cible {v}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {h.key === "weekly" && (
                    <>
                      <h2 className="font-serif text-xl mt-1" style={{ color: "#0B2545" }}>
                        Les 3 focus de cette semaine
                      </h2>
                      <div className="mt-2 space-y-2">
                        {(layer.focus_areas || []).map((f, i) => (
                          <div key={i} className="p-2 rounded border"
                                style={{ borderColor: h.accent }}
                                data-testid={`coach-focus-${i}`}>
                            <div className="font-semibold text-[14px]" style={{ color: "#0B2545" }}>
                              {f.title_fr}
                            </div>
                            <div className="text-[12px] text-slate-600 mt-0.5">
                              Cible · {f.target_fr}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                              Dérivé de · {f.derived_from}
                            </div>
                          </div>
                        ))}
                      </div>
                      {(layer.protected_blocks_fr || []).length > 0 && (
                        <div className="mt-3 text-[13px]">
                          <div className="uppercase text-[10px] tracking-widest font-bold" style={{ color: "#7A5B14" }}>
                            Blocs protégés
                          </div>
                          {layer.protected_blocks_fr.map((b, i) => (
                            <div key={i} className="text-slate-800">🔒 {b}</div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {h.key === "daily" && (
                    <>
                      <h2 className="font-serif text-xl mt-1" style={{ color: "#0B2545" }}>
                        Les 3 actions du jour
                      </h2>
                      <div className="mt-2 space-y-2">
                        {(layer.top_3_actions || []).map((a, i) => (
                          <div key={a.opportunity_id || i}
                                className="p-2 rounded border flex items-start justify-between gap-3"
                                style={{ borderColor: h.accent }}
                                data-testid={`coach-daily-${i}`}>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-[14px] truncate" style={{ color: "#0B2545" }}>
                                {i + 1}. {a.title_fr}
                              </div>
                              <div className="text-[12px] text-slate-600">
                                {a.recommended_action_fr}
                              </div>
                            </div>
                            <span className="text-[11px] font-mono px-2 py-1 rounded whitespace-nowrap"
                                  style={{ backgroundColor: "#0B2545", color: "#C5A880" }}>
                              Impact {a.impact_score}
                            </span>
                          </div>
                        ))}
                        {(layer.top_3_actions || []).length === 0 && (
                          <div className="text-slate-500 italic text-[13px]">
                            Aucune action disponible aujourd'hui — ouvrez le Radar pour en ajouter.
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="mt-3 pt-3 border-t text-[12px] text-slate-600 italic"
                        style={{ borderTopColor: h.accent }}>
                    <span className="uppercase text-[10px] tracking-widest not-italic font-bold"
                          style={{ color: h.tone }}>Pourquoi ce niveau · </span>
                    {layer.why_fr}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Blocking habits section */}
      {blocking && blocking.total > 0 && (
        <div className="max-w-6xl mx-auto px-8 pb-10">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden"
                data-testid="abi-coach-blocking-habits">
            <div className="p-6 border-b" style={{ borderColor: "#E5E7EB",
                  background: "linear-gradient(120deg,#7F1D1D 0%,#B91C1C 100%)" }}>
              <div className="flex items-start gap-3 text-white">
                <div className="w-10 h-10 rounded flex items-center justify-center"
                      style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}>
                  <ShieldAlert size={20} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-80">
                    Habitudes bloquantes détectées · fenêtre 90 jours
                  </div>
                  <h3 className="font-serif text-xl mt-0.5">
                    {blocking.total} cycle{blocking.total > 1 ? "s" : ""} répétitif{blocking.total > 1 ? "s" : ""} freine{blocking.total > 1 ? "nt" : ""} votre progression
                  </h3>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {blocking.findings.map((h, i) => (
                <div key={h.habit_id} data-testid={`coach-blocking-${i}`}
                      className="rounded-lg p-4 border"
                      style={{ borderColor: "#FEE2E2", backgroundColor: "#FEF2F2" }}>
                  <div className="font-serif text-lg mb-2" style={{ color: "#7F1D1D" }}>
                    {h.title_fr}
                  </div>
                  <div className="space-y-1 text-[13px] leading-relaxed">
                    <div>
                      <span className="font-bold" style={{ color: "#0B2545" }}>① Pourquoi · </span>
                      {h.explanation.why_fr}
                    </div>
                    <div>
                      <span className="font-bold" style={{ color: "#0B2545" }}>② Données · </span>
                      {h.explanation.evidence_fr.join(" · ")}
                    </div>
                    <div>
                      <span className="font-bold" style={{ color: "#0B2545" }}>③ Impact attendu · </span>
                      {h.explanation.expected_impact_fr}
                    </div>
                    <div>
                      <span className="font-bold" style={{ color: "#0B7A3B" }}>④ Ce que font les meilleurs · </span>
                      {h.explanation.top_performers_do_fr}
                    </div>
                    <div>
                      <span className="font-bold" style={{ color: "#B91C1C" }}>⑤ Risque si non traité · </span>
                      {h.explanation.risk_if_ignored_fr}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
